/**
 * @fileoverview Hook para pesquisa global consistente em toda a aplicação.
 * Centraliza a lógica de busca para garantir comportamento uniforme.
 * 
 * @module useGlobalSearch
 * @since 1.0.0
 */

import { useMemo } from 'react';
import { useLocalData } from './useLocalData';

/**
 * Hook para pesquisa global consistente.
 * Garante que a busca sempre considere TODOS os dados disponíveis do domínio.
 * 
 * @returns {Object} Funções de pesquisa globais
 */
export function useGlobalSearch() {
  const { products, categories, brands, promotions } = useLocalData();

  /**
   * Busca produtos em TODOS os produtos disponíveis.
   * @param {string} searchTerm - Termo de busca
   * @returns {Array} Produtos encontrados
   */
  const searchAllProducts = useMemo(() => {
    return (searchTerm) => {
      if (!searchTerm || searchTerm.trim() === '') return [];
      
      const query = searchTerm.toLowerCase();
      // filter: busca em TODOS os produtos, não apenas nos exibidos
      return products.filter(product => 
        product.nome?.toLowerCase().includes(query) ||
        product.descricao?.toLowerCase().includes(query) ||
        product.marca?.toLowerCase().includes(query) ||
        product.categoria?.toLowerCase().includes(query)
      );
    };
  }, [products]);

  /**
   * Busca categorias em TODAS as categorias disponíveis.
   * @param {string} searchTerm - Termo de busca
   * @returns {Array} Categorias encontradas
   */
  const searchAllCategories = useMemo(() => {
    return (searchTerm) => {
      if (!searchTerm || searchTerm.trim() === '') return categories;
      
      const query = searchTerm.toLowerCase();
      // filter: busca em TODAS as categorias disponíveis
      return categories.filter(category => 
        category.name?.toLowerCase().includes(query)
      );
    };
  }, [categories]);

  /**
   * Busca marcas em TODAS as marcas disponíveis.
   * @param {string} searchTerm - Termo de busca
   * @returns {Array} Marcas encontradas
   */
  const searchAllBrands = useMemo(() => {
    return (searchTerm) => {
      if (!searchTerm || searchTerm.trim() === '') return brands;
      
      const query = searchTerm.toLowerCase();
      // filter: busca em TODAS as marcas disponíveis
      return brands.filter(brand => 
        brand.name?.toLowerCase().includes(query)
      );
    };
  }, [brands]);

  /**
   * Busca promoções em TODAS as promoções disponíveis.
   * @param {string} searchTerm - Termo de busca
   * @returns {Array} Promoções encontradas
   */
  const searchAllPromotions = useMemo(() => {
    return (searchTerm) => {
      if (!searchTerm || searchTerm.trim() === '') return promotions;
      
      const query = searchTerm.toLowerCase();
      // filter: busca em TODAS as promoções disponíveis
      return promotions.filter(promotion => 
        promotion.name?.toLowerCase().includes(query) ||
        promotion.context?.description?.toLowerCase().includes(query)
      );
    };
  }, [promotions]);

  /**
   * Limpa resultados de pesquisa e retorna dados completos.
   * @returns {Object} Todos os dados disponíveis
   */
  const clearAllSearches = useMemo(() => {
    return () => ({
      products,
      categories,
      brands,
      promotions
    });
  }, [products, categories, brands, promotions]);

  /**
   * Verifica se há dados disponíveis para pesquisa.
   * @returns {Object} Status de disponibilidade
   */
  const hasSearchableData = useMemo(() => {
    return {
      products: products.length > 0,
      categories: categories.length > 0,
      brands: brands.length > 0,
      promotions: promotions.length > 0
    };
  }, [products.length, categories.length, brands.length, promotions.length]);

  return {
    // Funções de pesquisa global
    searchAllProducts,
    searchAllCategories,
    searchAllBrands,
    searchAllPromotions,
    
    // Utilitários
    clearAllSearches,
    hasSearchableData,
    
    // Dados base (para referência)
    totalCounts: {
      products: products.length,
      categories: categories.length,
      brands: brands.length,
      promotions: promotions.length
    }
  };
}

export default useGlobalSearch;